import { cx } from "@light-technology/components";
import type { AnimationOptions, DOMKeyframesDefinition } from "motion/react";
import { animate } from "motion/react";
import { useEffect, useRef, useState } from "react";

const ANIMATIONS = ["shuffle", "orbit", "bounce", "converge", "float"] as const;

type Animation = (typeof ANIMATIONS)[number];

// Duration of one full animation cycle in ms.
const DURATIONS: Record<Animation, number> = {
  shuffle: 1200,
  orbit: 2000,
  bounce: 1500,
  converge: 2400,
  float: 3000,
};

// Minimum hold time before switching; actual switch lands on a cycle boundary.
const HOLD_MS = 2500;
// Duration of the ease-back transition before each new animation starts.
const EASE_BACK_MS = 200;

const SPRING = [0.34, 1.56, 0.64, 1] as const;
const SPRING_SOFT = [0.34, 1.15, 0.64, 1] as const;
const EASE_STRONG = [0.55, 0, 0.45, 1] as const;

const INF_LOOP = {
  repeat: Infinity,
  repeatType: "loop",
} as const satisfies AnimationOptions;

// Keep in sync with tailwind size-* class names.
const DOT_SIZE = 16;
const DOT_GAP = -4;
const DOT_SPACING = DOT_SIZE + DOT_GAP;

type DotCfg = {
  animate: DOMKeyframesDefinition;
  transition: AnimationOptions;
};
type AnimConfig = { yellow: DotCfg; magenta: DotCfg; cyan: DotCfg };

/**
 * Generate a dot config that orbits around the center with a given initial angle.
 */
const orbitDot = (
  initialAngle: number,
  baseLeft: number,
  steps = 40,
): DotCfg => {
  const x: number[] = [];
  const y: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = Math.PI - (i / steps) * 2 * Math.PI + initialAngle;
    x.push(DOT_SPACING + DOT_SPACING * Math.cos(a) - baseLeft);
    y.push(-DOT_SPACING * Math.sin(a));
  }
  return {
    animate: { x, y },
    transition: {
      duration: DURATIONS.orbit / 1000,
      ease: "linear",
      ...INF_LOOP,
    },
  };
};

// Configs for all non-orbit animations (driven imperatively via animate()).
const CONFIGS: Record<Animation, AnimConfig> = {
  // Yellow and cyan rotate around magenta in the center.
  orbit: {
    yellow: orbitDot(0, 0),
    magenta: { animate: { x: 0, y: 0 }, transition: {} },
    cyan: orbitDot(Math.PI, DOT_SPACING * 2),
  },
  // Each dot moves horizontally, then back, then to the other's position, then
  // back, creating a "shuffling" effect.
  shuffle: {
    yellow: {
      animate: { x: [0, 0, DOT_SPACING * 2, DOT_SPACING, 0], y: 0 },
      transition: {
        duration: DURATIONS.shuffle / 1000,
        times: [0, 0.25, 0.5, 0.75, 1],
        ease: [SPRING, SPRING, SPRING, SPRING],
        ...INF_LOOP,
      },
    },
    magenta: {
      animate: { x: [0, DOT_SPACING, -DOT_SPACING, -DOT_SPACING, 0], y: 0 },
      transition: {
        duration: DURATIONS.shuffle / 1000,
        times: [0, 0.25, 0.5, 0.75, 1],
        ease: [SPRING, SPRING, SPRING, SPRING],
        ...INF_LOOP,
      },
    },
    cyan: {
      animate: { x: [0, -DOT_SPACING, -DOT_SPACING, 0, 0], y: 0 },
      transition: {
        duration: DURATIONS.shuffle / 1000,
        times: [0, 0.25, 0.5, 0.75, 1],
        ease: [SPRING, SPRING, SPRING, SPRING],
        ...INF_LOOP,
      },
    },
  },
  // Each dot starts rising before the previous one finishes falling, creating overlap.
  bounce: {
    yellow: {
      animate: { x: 0, y: [0, -DOT_SIZE * 0.5625, 0, 0] },
      transition: {
        duration: DURATIONS.bounce / 1000,
        times: [0, 0.12, 0.28, 1],
        ease: SPRING_SOFT,
        ...INF_LOOP,
      },
    },
    magenta: {
      animate: { x: 0, y: [0, 0, -DOT_SIZE * 0.75, 0, 0] },
      transition: {
        duration: DURATIONS.bounce / 1000,
        times: [0, 0.2, 0.45, 0.62, 1],
        ease: SPRING_SOFT,
        ...INF_LOOP,
      },
    },
    cyan: {
      animate: { x: 0, y: [0, 0, -DOT_SIZE * 0.5625, 0, 0] },
      transition: {
        duration: DURATIONS.bounce / 1000,
        times: [0, 0.45, 0.67, 0.83, 1],
        ease: SPRING_SOFT,
        ...INF_LOOP,
      },
    },
  },
  // Two dots swap places by passing through the center, while the third stays
  // put, creating a "converging" effect.
  converge: {
    yellow: {
      animate: {
        x: [
          0,
          DOT_SPACING,
          DOT_SPACING,
          DOT_SPACING * 2,
          DOT_SPACING * 2,
          DOT_SPACING,
          DOT_SPACING,
          0,
        ],
        y: 0,
      },
      transition: {
        duration: DURATIONS.converge / 1000,
        times: [0, 0.2, 0.28, 0.48, 0.56, 0.76, 0.84, 1],
        ease: [
          EASE_STRONG,
          "linear",
          SPRING,
          "linear",
          EASE_STRONG,
          "linear",
          SPRING,
        ],
        ...INF_LOOP,
      },
    },
    magenta: {
      animate: { x: 0, y: 0 },
      transition: {},
    },
    cyan: {
      animate: {
        x: [
          0,
          -DOT_SPACING,
          -DOT_SPACING,
          -DOT_SPACING * 2,
          -DOT_SPACING * 2,
          -DOT_SPACING,
          -DOT_SPACING,
          0,
        ],
        y: 0,
      },
      transition: {
        duration: DURATIONS.converge / 1000,
        times: [0, 0.2, 0.28, 0.48, 0.56, 0.76, 0.84, 1],
        ease: [
          EASE_STRONG,
          "linear",
          SPRING,
          "linear",
          EASE_STRONG,
          "linear",
          SPRING,
        ],
        ...INF_LOOP,
      },
    },
  },
  // Wave pulse rippling left→right. Each dot rises above then dips below
  // baseline. All share the same period and start/end at y=0.
  float: {
    yellow: {
      animate: { x: 0, y: [0, -DOT_SIZE * 0.625, DOT_SIZE * 0.25, 0, 0] },
      transition: {
        duration: DURATIONS.float / 1000,
        times: [0, 0.15, 0.38, 0.55, 1],
        ease: "easeInOut",
        ...INF_LOOP,
      },
    },
    magenta: {
      animate: { x: 0, y: [0, 0, -DOT_SIZE * 0.625, DOT_SIZE * 0.25, 0, 0] },
      transition: {
        duration: DURATIONS.float / 1000,
        times: [0, 0.28, 0.48, 0.68, 0.82, 1],
        ease: "easeInOut",
        ...INF_LOOP,
      },
    },
    cyan: {
      animate: { x: 0, y: [0, 0, -DOT_SIZE * 0.625, DOT_SIZE * 0.25, 0] },
      transition: {
        duration: DURATIONS.float / 1000,
        times: [0, 0.55, 0.75, 0.9, 1],
        ease: "easeInOut",
        ...INF_LOOP,
      },
    },
  },
};

export const ChatLoader = () => {
  const [animation, setAnimation] = useState<Animation>(
    () => ANIMATIONS[Math.floor(Math.random() * ANIMATIONS.length)],
  );
  const animationRef = useRef(animation);

  const yellowRef = useRef<HTMLDivElement>(null);
  const magentaRef = useRef<HTMLDivElement>(null);
  const cyanRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const y = yellowRef.current;
    const m = magentaRef.current;
    const c = cyanRef.current;
    if (!y || !m || !c) return;

    let cancelled = false;
    const cfg = CONFIGS[animation];

    const run = async () => {
      // Ease all dots back to base before starting the next animation so
      // transitions are always clean regardless of where dots are mid-cycle.
      await Promise.all([
        animate(y, { x: 0, y: 0 }, { duration: 0.2, ease: "easeOut" }),
        animate(m, { x: 0, y: 0 }, { duration: 0.2, ease: "easeOut" }),
        animate(c, { x: 0, y: 0 }, { duration: 0.2, ease: "easeOut" }),
      ]);
      if (cancelled) return;
      animate(y, cfg.yellow.animate, cfg.yellow.transition);
      animate(m, cfg.magenta.animate, cfg.magenta.transition);
      animate(c, cfg.cyan.animate, cfg.cyan.transition);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [animation]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      const dur = DURATIONS[animationRef.current];
      // Round up to the next full cycle boundary ≥ HOLD_MS, then add the
      // ease-back duration so the timeout fires at a true cycle boundary
      // relative to when the animation actually started playing.
      const wait = Math.ceil(HOLD_MS / dur) * dur + EASE_BACK_MS;

      timeout = setTimeout(() => {
        const others = ANIMATIONS.filter((a) => a !== animationRef.current);
        const next = others[Math.floor(Math.random() * others.length)];
        animationRef.current = next;
        setAnimation(next);
        scheduleNext();
      }, wait);
    };

    scheduleNext();
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const DOT_BASE = cx("absolute size-4 rounded-full mix-blend-multiply");

  return (
    <div className="relative size-10">
      <div
        ref={yellowRef}
        className={cx(DOT_BASE, "bg-brand-accent-yellow")}
        style={{ left: 0, top: DOT_SPACING }}
      />
      <div
        ref={magentaRef}
        className={cx(DOT_BASE, "bg-brand-accent-magenta")}
        style={{ left: DOT_SPACING, top: DOT_SPACING }}
      />
      <div
        ref={cyanRef}
        className={cx(DOT_BASE, "bg-brand-accent-cyan")}
        style={{ left: DOT_SPACING * 2, top: DOT_SPACING }}
      />
    </div>
  );
};
